import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import PropertySorting from "./PropertySorting";
import SearchIcon from "@mui/icons-material/Search";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useEffect, useState } from "react";
import PropertyRange from "./PropertyRange";
import { useTranslation } from "react-i18next";
import { PROPERTY_OVERVIEW_SORT_BY } from "../../utils/constants";

interface PropertyFilterModalProps {
  onApplyFilter: (filter: any) => void;
  isLoading: boolean;
}

const PropertyFilterModal = ({
  onApplyFilter,
  isLoading,
}: PropertyFilterModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState<boolean>(!isSmallScreen);
  const [location, setLocation] = useState<string>("");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 6000]);
  const [roomCount, setRoomCount] = useState<[number, number]>([1, 6]);
  const [newFilter, setNewFilter] = useState<any>({});
  const [sortDropdownIndex, setSortDropdownIndex] = useState<any>(1);
  const [reset, setReset] = useState<boolean>(false);

  const handleClick = () => {
    setOpen(!open);
  };

  const handleReset = () => {
    setLocation("");
    setPriceRange([0, 6000]);
    setRoomCount([1, 6]);
    setSortDropdownIndex(1);

    handleApply();
  };

  useEffect(() => {
    if (reset) {
      handleReset();
      setReset(false);
    }
  }, [reset]);

  useEffect(() => {
    const updatedFilter: any = {};

    if (location.trim() !== "") {
      updatedFilter.location = location;
    }
    if (priceRange[0] !== 0 || priceRange[1] !== 6000) {
      updatedFilter.minPrice = priceRange[0];
      updatedFilter.maxPrice = priceRange[1];
    }
    if (roomCount[0] !== 0 || roomCount[1] !== 8) {
      updatedFilter.minRooms = roomCount[0];
      updatedFilter.maxRooms = roomCount[1];
    }

    const currentSortField = PROPERTY_OVERVIEW_SORT_BY[sortDropdownIndex - 1];
    updatedFilter.sortField = currentSortField.sortField;
    updatedFilter.sortOrder = currentSortField.sortOrder;

    setNewFilter(updatedFilter);
  }, [location, priceRange, roomCount, sortDropdownIndex]);

  const handleApply = () => {
    setNewFilter({});
    onApplyFilter(newFilter);
  };

  return (
    <Grid
      position={"sticky"}
      top={120}
      padding={2}
      sx={{
        background: "#fff",
        borderRadius: 4,
        boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
      }}
      item
      xs={12}
      maxHeight={600}
    >
      <Grid paddingBottom={isSmallScreen ? 0 : 3} item xs={12} bgcolor={"#fff"}>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          zIndex={60}
          bgcolor={"#fff"}
          onClick={() => (isSmallScreen ? handleClick() : undefined)}
        >
          <Box
            display={"flex"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            gap={1}
          >
            <Box>
              <SearchIcon color="primary" />
            </Box>
            <Box>
              <Typography variant="h3" fontWeight={"bold"}>
                Filters
              </Typography>
            </Box>
          </Box>
          {isSmallScreen && (
            <Box>
              <Button onClick={handleClick}>
                {open ? <ExpandLess /> : <ExpandMore />}
              </Button>
            </Box>
          )}
        </Box>
      </Grid>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          padding={1}
          paddingTop={2}
          height={780}
          margin={"auto"}
          sx={{
            overflow: "scroll",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Grid item xs={12}>
            <PropertySorting value={sortDropdownIndex} setValue={setSortDropdownIndex} />
          </Grid>
          <Grid item xs={12} paddingTop={2}>
            <Typography variant="body1" marginTop={1}>
              {t("overview.location")}
            </Typography>
            <TextField
              fullWidth
              id="location"
              variant="outlined"
              placeholder={t("overview.city")}
              size="small"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnOutlinedIcon fontSize="inherit" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} padding={1}>
            <Typography variant="body1" marginTop={2} marginLeft={"-.5rem"}>
              {t("overview.price")}
            </Typography>
            <PropertyRange
              labelMax={t("overview.maximum")}
              labelMin={t("overview.minimum")}
              maxNumber={6000}
              minValue={0}
              step={100}
              value={priceRange}
              setValue={setPriceRange}
            />
          </Grid>
          <Grid item xs={12} padding={1}>
            <Typography variant="body1" marginTop={2} marginLeft={"-.5rem"}>
              {t("overview.rooms")}
            </Typography>
            <PropertyRange
              labelMax={t("overview.maximum")}
              labelMin={t("overview.minimum")}
              maxNumber={6}
              minValue={1}
              step={1}
              value={roomCount}
              setValue={setRoomCount}
            />
          </Grid>
          <Grid
            item
            xs={12}
            padding={1}
            display={"flex"}
            justifyContent={"space-between"}
          >
            <Button
              variant="outlined"
              sx={{
                marginLeft: "-.5rem",
                "&:hover": {
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                },
              }}
              onClick={() => {
                setReset(true);
              }}
            >
              Reset
            </Button>
            <Button
              variant="outlined"
              sx={{
                marginRight: "-.2rem",
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                width: 150,
                "&:hover": {
                  color: theme.palette.primary.main,
                },
              }}
              onClick={() =>
                isSmallScreen ? [handleClick(), handleApply()] : handleApply()
              }
            >
              {isLoading ? (
                <CircularProgress
                  size={20}
                  sx={{
                    color: "#fff",
                  }}
                />
              ) : (
                t("overview.apply_filter")
              )}
            </Button>
          </Grid>
        </Box>
      </Collapse>
    </Grid>
  );
};

export const Subheadline = styled(Typography)({
  fontSize: 16,
  fontWeight: 600,
  marginBottom: "0.5rem",
});

export default PropertyFilterModal;
